import { useId, useMemo } from 'react';
import { CircleAlert, Loader2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '@/components/app';
import { PlanDialogProps } from '../types';
import {
  type DurationUnit,
  byDuration,
  daysLabel,
  durationDays,
  groupDigits,
  parseDigits,
  perDay,
  uzDurationTitle,
} from '../planFormat';

const BASE_VARIANT = 'base';

const UNITS: { value: DurationUnit; label: string }[] = [
  { value: 'week', label: 'Неделя' },
  { value: 'month', label: 'Месяц' },
  { value: 'year', label: 'Год' },
];

const CURRENCY_LABELS: Record<string, string> = { UZS: 'UZS — сум' };

/** «Новый тариф» / «Редактировать тариф» — форма слева, предпросмотр экрана оплаты справа (макет «Тарифы»). */
export function PlanDialog({
  plans,
  editingPlanId = null,
  variants,
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
  isSubmitting = false,
}: PlanDialogProps) {
  const ids = useId().replace(/:/g, '');
  const days = durationDays(formData.duration_value, formData.duration_unit);
  const oldPriceError =
    formData.old_price != null && formData.old_price > 0 && formData.old_price <= formData.price
      ? `Старая цена не больше цены. В приложении она зачёркнута как «было», поэтому должна быть больше ${groupDigits(formData.price)} сум или пустой.`
      : null;

  const variantId = formData.variant_id ?? null;
  const currency = formData.currency || 'UZS';
  const currencies = currency === 'UZS' ? ['UZS'] : ['UZS', currency];

  // Предпросмотр: активные планы того же прайс-листа + текущий план из формы (выделен).
  const preview = useMemo(() => {
    const others = plans.filter(
      (p) => p.id !== editingPlanId && p.is_active && (p.variant_id ?? null) === variantId,
    );
    const draft = {
      id: '__draft__',
      title: formData.title,
      duration_value: formData.duration_value,
      duration_unit: formData.duration_unit,
      price: formData.price,
      old_price: formData.old_price ?? null,
      is_active: formData.is_active ?? true,
    };
    return [...others.map((p) => ({ ...p, isDraft: false })), { ...draft, isDraft: true }].sort(byDuration);
  }, [plans, editingPlanId, variantId, formData]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-[940px] gap-0 overflow-y-auto p-0">
        <DialogHeader bordered>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap">
          <form
            className="flex min-w-0 flex-[1_1_460px] flex-col gap-4 px-6 py-5"
            onSubmit={(e) => {
              e.preventDefault();
              if (!oldPriceError) void onSubmit();
            }}
          >
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
              <FormField label="Код" required hint="Латиница, цифры и _">
                <Input
                  value={formData.code}
                  onChange={(e) => onFormDataChange((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="PREMIUM_1M"
                  className="font-mono text-[13px]"
                />
              </FormField>
              <FormField label="Название" required>
                <Input
                  value={formData.title}
                  onChange={(e) => onFormDataChange((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Premium 1 месяц"
                />
              </FormField>
            </div>

            <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0">
              <legend className="pb-1.5 text-[13px] font-semibold text-text">
                Длительность <span className="ml-1 font-medium text-text-2">обязательно</span>
              </legend>
              <div className="flex flex-wrap gap-2.5">
                <label htmlFor={`${ids}-dur`} className="sr-only">
                  Количество
                </label>
                <Input
                  id={`${ids}-dur`}
                  type="number"
                  min={1}
                  value={formData.duration_value}
                  onChange={(e) =>
                    onFormDataChange((prev) => ({ ...prev, duration_value: parseInt(e.target.value) || 1 }))
                  }
                  className="w-[88px] tabular-nums"
                />
                <div role="radiogroup" aria-label="Единица" className="flex gap-1 rounded-xl bg-muted p-1">
                  {UNITS.map((u) => {
                    const checked = formData.duration_unit === u.value;
                    return (
                      <button
                        key={u.value}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        onClick={() => onFormDataChange((prev) => ({ ...prev, duration_unit: u.value }))}
                        className={cn(
                          'h-8 rounded-[9px] px-3.5 text-sm outline-none transition-[background-color,color] focus-visible:ring-2 focus-visible:ring-ring',
                          checked ? 'bg-surface font-bold text-text shadow-card' : 'font-semibold text-text-2 hover:text-text',
                        )}
                      >
                        {u.label}
                      </button>
                    );
                  })}
                </div>
                <span className="self-center text-[13px] tabular-nums text-text-2">= {daysLabel(days)} доступа</span>
              </div>
            </fieldset>

            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
              <FormField label="Цена" required>
                <MoneyInput
                  value={formData.price || null}
                  onChange={(v) => onFormDataChange((prev) => ({ ...prev, price: v ?? 0 }))}
                  className="font-display font-bold"
                />
              </FormField>
              <FormField label="Старая цена" hint={oldPriceError ? undefined : 'Зачёркнутая цена «было»'}>
                <MoneyInput
                  value={formData.old_price ?? null}
                  invalid={!!oldPriceError}
                  describedBy={oldPriceError ? `${ids}-old-err` : undefined}
                  onChange={(v) => onFormDataChange((prev) => ({ ...prev, old_price: v }))}
                />
              </FormField>
              <FormField label="Валюта" hint="Оплата в приложении — только в сумах">
                {(p) => (
                  <Select value={currency} onValueChange={(v) => onFormDataChange((prev) => ({ ...prev, currency: v }))}>
                    <SelectTrigger {...p}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => (
                        <SelectItem key={c} value={c}>
                          {CURRENCY_LABELS[c] ?? c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </FormField>
            </div>

            {oldPriceError && (
              <div
                id={`${ids}-old-err`}
                role="alert"
                className="-mt-1 flex items-start gap-2 rounded-xl bg-neg-soft px-3 py-2.5 text-[13px] font-semibold text-neg"
              >
                <CircleAlert className="mt-px size-4 shrink-0" aria-hidden />
                <span>{oldPriceError}</span>
              </div>
            )}

            <div className="grid grid-cols-1 items-start gap-3.5 sm:grid-cols-2">
              <FormField label="Куда входит план" hint="План входит в прайс-лист или ровно в один вариант">
                {(p) => (
                  <Select
                    value={variantId ?? BASE_VARIANT}
                    onValueChange={(value) =>
                      onFormDataChange((prev) => ({ ...prev, variant_id: value === BASE_VARIANT ? null : value }))
                    }
                  >
                    <SelectTrigger {...p}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={BASE_VARIANT}>Базовый прайс-лист</SelectItem>
                      {variants
                        .filter((v) => v.status !== 'archived')
                        .map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            Ценовой вариант «{v.name}»
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                )}
              </FormField>
              <div className="flex items-center justify-between gap-3 rounded-xl border border-border-soft bg-surface-alt px-3 py-2.5 sm:mt-6">
                <span className="flex flex-col gap-0.5">
                  <span id={`${ids}-active`} className="text-sm font-semibold">
                    Активен
                  </span>
                  <span className="text-xs text-text-2">Показывать на экране оплаты</span>
                </span>
                <Switch
                  aria-labelledby={`${ids}-active`}
                  checked={formData.is_active}
                  onCheckedChange={(checked) => onFormDataChange((prev) => ({ ...prev, is_active: checked }))}
                />
              </div>
            </div>
            {/* Enter в поле отправляет форму. */}
            <button type="submit" hidden aria-hidden tabIndex={-1} />
          </form>

          <aside
            aria-label="Предпросмотр в приложении"
            className="flex min-w-0 flex-[1_1_300px] flex-col gap-3 border-t border-border-soft bg-surface-alt px-6 py-5 sm:border-l sm:border-t-0"
          >
            <div className="flex items-center justify-between">
              <b className="font-display text-[15px]">Как увидит пользователь</b>
              <span className="text-xs text-text-2">Экран оплаты</span>
            </div>
            <div className="flex flex-col gap-2.5 rounded-card border border-border-soft bg-bg p-3.5">
              <span className="font-display text-[17px] font-extrabold">Premium tanlang</span>
              {preview.map((p) => {
                const d = durationDays(p.duration_value, p.duration_unit);
                const daily = perDay(p.price, d);
                const showOld = p.old_price != null && p.old_price > p.price;
                const hidden = p.isDraft && !p.is_active;
                return (
                  <div
                    key={p.id}
                    className={cn(
                      'flex items-center justify-between gap-2.5 rounded-2xl bg-surface px-3.5 py-3',
                      p.isDraft ? 'ring-2 ring-inset ring-accent' : 'ring-1 ring-inset ring-border',
                      hidden && 'opacity-50',
                    )}
                  >
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <b className="font-display text-[15px]">{uzDurationTitle(p.duration_value, p.duration_unit)}</b>
                      <span className="text-xs tabular-nums text-text-2">
                        {daily !== null ? `kuniga ${groupDigits(daily)} so‘m` : '—'}
                      </span>
                    </span>
                    <span className="flex flex-col items-end gap-0.5">
                      <b className="whitespace-nowrap font-display text-base tabular-nums">
                        {p.price > 0 ? `${groupDigits(p.price)} so‘m` : <span className="text-text-3">— so‘m</span>}
                      </b>
                      {showOld && <s className="text-xs tabular-nums text-text-3">{groupDigits(p.old_price!)}</s>}
                    </span>
                  </div>
                );
              })}
              <span className="grid h-11 place-items-center rounded-[14px] bg-inverse font-bold text-on-inverse">
                Davom etish
              </span>
            </div>
            <p className="m-0 text-xs leading-normal text-text-2">
              {editingPlanId ? 'Редактируемый план выделен.' : 'Новый план выделен.'}
              {!formData.is_active && ' Пока план неактивен, пользователи его не увидят.'} Старая цена не
              показывается, пока она не больше цены. Цена в день = цена ÷ дни доступа, округление вниз.
            </p>
          </aside>
        </div>

        <DialogFooter bordered>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} disabled={isSubmitting || !!oldPriceError}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface MoneyInputProps {
  id?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  invalid?: boolean;
  describedBy?: string;
  className?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  'aria-required'?: true;
}

/** Сумма с разрядами «119 900» и подписью «сум» внутри поля. id/aria приходят из FormField. */
function MoneyInput({ value, onChange, invalid, describedBy, className, ...aria }: MoneyInputProps) {
  const described = [aria['aria-describedby'], describedBy].filter(Boolean).join(' ') || undefined;
  return (
    <span className="relative block">
      <Input
        {...aria}
        inputMode="numeric"
        autoComplete="off"
        value={value != null ? groupDigits(value) : ''}
        onChange={(e) => onChange(parseDigits(e.target.value))}
        aria-invalid={invalid || aria['aria-invalid'] || undefined}
        aria-describedby={described}
        className={cn('pr-11 tabular-nums', className)}
      />
      <span aria-hidden className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-text-2">
        сум
      </span>
    </span>
  );
}
